import Controller from "csi/action/Controller";
import CustomViewAttribute from "csi/middleware/customView/attributes/CustomViewAttribute";
import CustomViewAttributesStore from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import { CustomViewNodeContexts } from "csi/middleware/customView/customViewLeftPanel/CustomViewNodeContexts";
import CustomViewDataManagementStore, { DataManagementOptions } from "csi/middleware/customView/data/CustomViewDataManagementStore";
import { ICustomViewQueryOptions, IViewSettingOptions } from "csi/middleware/customView/options/CustomViewOptions";
import CustomViewSettingsStore from "csi/middleware/customView/settings/CustomViewSettingsStore";
import localeProvider from "csi/page/localeProvider";
import csiPrefs from "csi/prefs";
import { routerStore } from "csi/state/rootStore";
import { isDefined } from "csi/utility/coreUtility";
import { DndStore } from "designSystem/dnd/index";
import { ILocaleProvider } from "designSystem/locale";
import declare from "dojo/_base/declare";
import { queryCustomViews } from "src/c8-platform/service/customViewBuilderService";
import CustomViewDialog from "../middleware/customView/CustomViewDialog";
import CustomViewDialogStore, { NewCustomViewFromNodeFunction } from "../middleware/customView/CustomViewDialogStore";

const CustomViewController = declare(Controller, {
	/** @property {CustomViewResult} availableCustomViews - available custom view for the current user */
	availableCustomViews: null,

	/** @property {CustomViewNodeContexts} customViewNodeContexts - object managing available node contexts */
	customViewNodeContexts: null,

	/** @property {Error} error - potential error */
	error: null,

	/** @property {boolean} forceDuplicate - indicate if the action is SaveAs or not */
	forceDuplicate: false,

	/**
	 * @override
	 * get the available custom views
	 */
	onPreActionDeferreds(dfds: Promise<void>[]): void {
		const { widget } = this;
		const { customViewContainerUrl } = widget;

		dfds.push(queryCustomViews(customViewContainerUrl, this.getRelativeType())
			.then(((results) => {
				this.availableCustomViews = results;
			}))
			.catch((error) => {
				this.error = error;
			}));

		// handle the node contexts
		const contextsStore = widget.getCustomViewNodeContextsStore() as any;
		this.customViewNodeContexts = new CustomViewNodeContexts(contextsStore?.typeNode, contextsStore?.stateNode);

		if (contextsStore) {
			const prefKey = widget.getViewPreferenceContext(false, true);
			contextsStore.query().forEach((item) => {
				this.customViewNodeContexts.addContext(csiPrefs.composeKey(prefKey, null, item[contextsStore.idProperty]), item[contextsStore.labelProperty]);
			});
		}
	},

	/**
	 * Extension point to populate dialog store
	 */
	onCreateStore(loc: ILocaleProvider, store: CustomViewDialogStore): void { },

	/**
	 * Extension point to populate custom view store 
	 */
	onCreateStoreOptions(store: CustomViewDialogStore): void {
		const { widget } = this;
		const { customViewOptionsComponent, customViewOptionsStore, prefSiteOnly } = this.widget;
		const formattedCustomOptions = widget.getModifiedCustomViewValues();
		const { prefName, prefDescription, prefIsShared, prefParty, ...CustomOptions } = formattedCustomOptions as IViewSettingOptions;
		const viewSettingOptions = { prefName, prefDescription, prefIsShared, prefParty };
		store.settingStore = new CustomViewSettingsStore(store, viewSettingOptions, prefSiteOnly);

		if (customViewOptionsStore) {
			const dropdownOptions = widget.getCustomViewDropDownOptions();
			store.optionsStore = new customViewOptionsStore(dropdownOptions, customViewOptionsComponent, store, CustomOptions);
		}
	},

	/**
	 * @override
	 * Apply new custom view to store and view
	 */
	postProcessSuccess(): void {
		const { forceDuplicate, widget } = this;

		if (forceDuplicate) {
			widget.set("customViewDirty", false);
		}

		widget.refreshDefaultCustomView(true);
	},

	/**
	 * @override
	 * Open the custom view dialog and suspend other parts of C8 until close or save
	 */
	async preProcess(): Promise<void> {
		const { availableCustomViews, customViewNodeContexts, error, forceDuplicate, m_cache, widget } = this;
		const { customViewContainerUrl, customViewContext, customViewId, customViewPathHints, defView, onSaveCustomView, manageSingleCustomView } = widget;
		const customViewNodeType = widget.getViewNodeType();
		const previewProps = widget.getCustomViewPreviewProps();
		const targetType = defView.getPageClass().type;
		const OOTBCustomView = widget.getDefaultCustomView();
		const filterRelativeType = this.getRelativeType();
		const newInstanceFromNode: NewCustomViewFromNodeFunction = (node, sourceNode) => widget.newCustomViewFromNode(node, sourceNode);

		const store = new CustomViewDialogStore(
			localeProvider,
			previewProps,
			manageSingleCustomView,
			m_cache,
			customViewNodeType,
			customViewId,
			availableCustomViews,
			customViewContainerUrl,
			customViewContext,
			customViewNodeContexts,
			customViewPathHints,
			targetType,
			onSaveCustomView,
			OOTBCustomView,
			newInstanceFromNode,
			forceDuplicate,
			filterRelativeType,
		);

		// handle the error if any
		if (error) {
			store.setError(error);
		}

		await this.onCreateStore(localeProvider, store);
		this.onCreateStoreOptions(store);

		// attribute store is created in onCreateStore, now setup list item dnd store
		const { attributesStore } = store;
		if (attributesStore) {
			const { canDrop } = attributesStore;
			const attributeDndStore = new DndStore<CustomViewAttribute>(canDrop, true);
			attributesStore.attributeDndStore = attributeDndStore;
		}

		await store.prepareToOpen(true);
		store.createTabs();

		this.useReactDialog(routerStore.setCenterFloatComponent(CustomViewDialog, {
			onCancel: async (): Promise<void> => {
				// set user default after we select cancel
				await store.settingStore.processDefaults(this.m_writer);
				widget.refreshDefaultCustomView(true);
				store.defaults.notify(store.changedContexts);
				this.onCancelReactDialog();
			},
			onSaveAndClose: async (): Promise<void> => {
				const cv = await store.customViewStore.saveCurrent(true);
				store.defaults.notify(store.changedContexts);

				if (cv) {
					this.validateAndProcess();
				}
			},
			store,
		}));
	},

	/**
	 * Return data management store for view if allowed
	 */
	_createDataManagementStore(loc: ILocaleProvider, attributesStore: CustomViewAttributesStore): CustomViewDataManagementStore | undefined {
		const { widget } = this;
		const queryLevels = widget.getConfigurableQueryLevels();
		const formattedCustomOptions = widget.getModifiedCustomViewValues() as ICustomViewQueryOptions;

		if (queryLevels && this.isDataManagementStoreAllowed(formattedCustomOptions)) {
			const relativeType = this.getRelativeType();
			const dataManagementOptions: DataManagementOptions = { queryLevels };
			widget.onCustomViewDataManagementOptions(dataManagementOptions);
			return new CustomViewDataManagementStore(loc, attributesStore, relativeType, formattedCustomOptions, dataManagementOptions);
		}
	},

	/** return the type used for relative filters if relative filtering is allowed in this view */
	getRelativeType(): string {
		return this.widget.getQueryRelativeContext?.()?.getNodeType();
	},

	/** return if data management store is allowed based on whether the settings exist */
	isDataManagementStoreAllowed(formattedCustomOptions: ICustomViewQueryOptions): boolean {
		const { widget } = this;

		// it's possible the conditions for these features changed after startup, so check just in case
		return (isDefined(formattedCustomOptions.filterSettings) && widget.canFilter()) ||
			(isDefined(formattedCustomOptions.groupSettings) && widget.canGroup()) ||
			(isDefined(formattedCustomOptions.sortSettings) && widget.canSort()) ||
			(isDefined(formattedCustomOptions.reportSettings) && widget.canReport());
	},
});

export = CustomViewController;